"use client"

import { Button, Card, Col, Row, Select } from "antd"
import ArrowLeftRightIconDownIcon from "../../../icons/ArrowLeftRightIcon"
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass, faPen } from "@fortawesome/free-solid-svg-icons"
import { CalendarOutlined, UserOutlined } from "@ant-design/icons"
import SelectCabinClass from "../../FormSearch/desktop/SelectCabinClass"
import SelectAirportFrom from "../../FormSearch/desktop/SelectAirportFrom"
import SelectAirportTo from "../../FormSearch/desktop/SelectAirportTo"
import { useAppDispatch, useAppSelector } from "@/lib/redux/hooks"
import { getFlightsAllAirlines } from "@/utils/flight/getFlightsAllAirlines"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTranslation } from "react-i18next"
import { FlightSearchPrefill } from "../landing/flightSearchPrefill"
import dayjs from "dayjs"
import SelectPeopleWrapper from "../../FormSearch/desktop/SelectPeople.wrapper"
import SelectDateFromToWrapper from "../../FormSearch/desktop/SelectDateFromTo.wrapper"
import { FlightSearchPopoverBoundary } from "../../FormSearch/components/SelectPlacement/FlightSearchPopoverBoundary"
import FilterFlightsByDate from "../components/FilterFlightsByDate"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { cabinClasses } from "@/constants/flight/cabinClass"
import Text from "@/components/common/Typography/Text"

type FormSearchFlightProps = {
  fixedPosition?: boolean
  prefill?: FlightSearchPrefill
}

const filterSuffixIcon = (
  <span className="flights__formsearch--filter-caret" aria-hidden>
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 1.25L5 5.25L9 1.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
)

const FormSearchFlight = (props: FormSearchFlightProps = { fixedPosition: true }) => {
  const { t } = useTranslation()
  const { getAirportCityName, getValidDayjs: teValidDayjs } = useTETranslation()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLDivElement>(null)
  const expandedFormHeightRef = useRef(0)
  const isStickingRef = useRef(false)
  const dispatch = useAppDispatch()
  const router = useTERouter()
  const checkedItinerary = useAppSelector(state => state.flight.checkedItinerary)
  const inputFlights = useAppSelector(state => state.flight.inputFlights)
  const isMultiCities = useAppSelector(state => state.flight.isMultiCities)
  const airports = useAppSelector((state) => state.flight.get_airports.data)
  const inputAdult = useAppSelector((state) => state.flight.inputAdult)
  const inputChildren = useAppSelector((state) => state.flight.inputChildren)
  const inputInfant = useAppSelector((state) => state.flight.inputInfant)
  const cabinClass = useAppSelector((state) => state.flight.cabinClass)
  const didPrefillRef = useRef(false)
  const [isSticking, setIsSticking] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [stickyFlowSpacerHeight, setStickyFlowSpacerHeight] = useState(0)
  const canStick = !!props.fixedPosition && !isMultiCities

  const outbound = inputFlights.find((input) => !input.isReturn) || inputFlights[0]
  const paxCount = (inputAdult || 0) + (inputChildren || 0) + (inputInfant || 0)
  const cabinLabel = cabinClass
    ? t(
      (
        Object.values(cabinClasses) as Array<{ code: string; name: string }>
      ).find((c) => c.code === cabinClass)?.name || "common.all",
    )
    : t("common.all")

  const routeLabel = useMemo(() => {
    if (!outbound) return ""
    const from =
      getAirportCityName(outbound.fromAirport, { shownCode: true }) ||
      outbound.fromAirport?.code ||
      ""
    const to =
      getAirportCityName(outbound.toAirport, { shownCode: true }) ||
      outbound.toAirport?.code ||
      ""
    return `${from} → ${to}`
  }, [outbound, getAirportCityName])

  const dateLabel = useMemo(() => {
    if (!outbound) return ""
    const from = teValidDayjs(outbound.dateFrom)
    if (!checkedItinerary || !outbound.dateTo) {
      return from.format("dddd, D [thg] M YYYY")
    }
    const to = teValidDayjs(outbound.dateTo)
    return `${from.format("dddd, D [thg] M YYYY")} - ${to.format("dddd, D [thg] M YYYY")}`
  }, [outbound, checkedItinerary, teValidDayjs])

  useEffect(() => {
    if (!canStick) {
      isStickingRef.current = false
      setIsSticking(false)
      document.documentElement.classList.remove("flights-search-sticking")
      return
    }
    const sentinel = sentinelRef.current
    if (!sentinel) return

    let animationFrame = 0
    const updateSticking = () => {
      animationFrame = 0
      const sticking = sentinel.getBoundingClientRect().bottom <= 8
      if (sticking === isStickingRef.current) return

      isStickingRef.current = sticking
      setIsSticking(sticking)
      if (!sticking) setIsEditing(false)
      document.documentElement.classList.toggle("flights-search-sticking", sticking)
    }
    const scheduleUpdate = () => {
      if (!animationFrame) animationFrame = requestAnimationFrame(updateSticking)
    }

    updateSticking()
    window.addEventListener("scroll", scheduleUpdate, { passive: true })
    window.addEventListener("resize", scheduleUpdate)
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame)
      window.removeEventListener("scroll", scheduleUpdate)
      window.removeEventListener("resize", scheduleUpdate)
      isStickingRef.current = false
      document.documentElement.classList.remove("flights-search-sticking")
    }
  }, [canStick])

  useEffect(() => {
    if (!props.prefill) return
    if (didPrefillRef.current) return

    const hasAirports = airports && Object.keys(airports).length > 0
    if (!hasAirports) return

    const normalizeCode = (value?: string) => String(value || "").trim().toUpperCase()
    const startCode = normalizeCode(props.prefill.startPoint ?? undefined)
    const endCode = normalizeCode(props.prefill.endPoint ?? undefined)

    const findAirport = (code: string) => {
      if (!code) return null
      return (airports as any)?.[code] || (Object.values(airports as any).find((a: any) => a?.code === code) as any)
    }

    dispatch(flightActions.updateModeSearchFlight({ isMultiCities: false }))
    dispatch(flightActions.changeCheckedItinerary(false))

    if (!startCode && !!endCode) {
      dispatch(
        flightActions.selectFromAirport({
          indexInputFlight: 0,
          fromAirport: undefined as any,
        })
      )
    }

    if (startCode) {
      const fromAirport = findAirport(startCode)
      if (fromAirport) dispatch(flightActions.selectFromAirport({ indexInputFlight: 0, fromAirport }))
    }

    if (endCode) {
      const toAirport = findAirport(endCode)
      if (toAirport) dispatch(flightActions.selectToAirport({ indexInputFlight: 0, toAirport }))
    }

    if (props.prefill.dateFrom) {
      const d = dayjs(props.prefill.dateFrom)
      dispatch(
        flightActions.changeDateFrom({
          indexInputFlight: 0,
          dateFrom: (d.isValid() ? d : dayjs()).format("YYYY-MM-DD"),
        })
      )
    }

    didPrefillRef.current = true
  }, [airports, dispatch, props.prefill])

  const onEditSearch = () => {
    setIsEditing(true)
  }

  const runSearch = () => {
    setIsEditing(false)
    getFlightsAllAirlines({
      onStart({ newSearchString }) {
        router.push(`/flights/result${newSearchString}`)
      },
    })
  }

  // Compact sticky bar; Edit → mở lại form search tại chỗ (không scroll lên đầu)
  const stickyCompact = canStick && isSticking && !isEditing
  const stickyExpanded = canStick && isSticking && isEditing

  useLayoutEffect(() => {
    const form = formRef.current
    if (!form) return

    const updateFlowSpacer = () => {
      const height = form.getBoundingClientRect().height
      if (!isSticking || isEditing) {
        expandedFormHeightRef.current = height
        setStickyFlowSpacerHeight(0)
        return
      }

      setStickyFlowSpacerHeight(Math.max(0, expandedFormHeightRef.current - height))
    }

    updateFlowSpacer()
    const observer = new ResizeObserver(updateFlowSpacer)
    observer.observe(form)
    return () => observer.disconnect()
  }, [isSticking, isEditing])

  return (
    <>
      {canStick && <div ref={sentinelRef} className="flights__formsearch-sentinel" aria-hidden />}
      <Row
        style={{ marginTop: canStick ? 0 : 15, zIndex: 3 }}
        ref={formRef}
        justify="center"
        className={`flights__formsearch${isMultiCities ? " flights__formsearch--multi" : ""}${!!props.fixedPosition ? "" : " flights__formsearch--disabled-fixed-potision"}${stickyCompact || stickyExpanded ? " sticking" : ""}${stickyExpanded ? " sticking--editing" : ""}`}
      >
        <Col span={24} className="flights__formsearch--box">
          {stickyCompact ? (
            <div className="flights__formsearch--sticky-compact">
              <div className="flights__formsearch--sticky-summary">
                <div className="flights__formsearch--sticky-route-row">
                  <Text strong className="flights__formsearch--sticky-route" ellipsis>
                    {t("flight.sticky_trip")} {routeLabel}
                  </Text>
                  <Button
                    type="default"
                    size="small"
                    className="flights__formsearch--sticky-edit"
                    icon={<FontAwesomeIcon icon={faPen} />}
                    onClick={onEditSearch}
                  >
                    {t("common.edit")}
                  </Button>
                </div>
                <div className="flights__formsearch--sticky-meta">
                  <span className="flights__formsearch--sticky-meta-item">
                    <CalendarOutlined />
                    <span>{dateLabel}</span>
                  </span>
                  <span className="flights__formsearch--sticky-meta-item">
                    <UserOutlined />
                    <span>
                      {t("flight.sticky_passengers", { count: Math.max(1, paxCount) })}
                      {" • "}
                      {cabinLabel}
                    </span>
                  </span>
                </div>
              </div>
              <div className="flights__formsearch--sticky-calendar">
                <FilterFlightsByDate variant="sticky" />
              </div>
            </div>
          ) : (
            <>
              <Row
                justify="space-between"
                align="middle"
                className="flights__formsearch--filters"
                wrap={false}
              >
                <Col>
                  <Row gutter={4} align="middle" wrap={false}>
                    {!isMultiCities && (
                      <Col>
                        <Select
                          className="flights__formsearch--select-type"
                          popupMatchSelectWidth={false}
                          variant="borderless"
                          defaultValue={"one_way"}
                          value={checkedItinerary ? "round_trip" : "one_way"}
                          onChange={(value: any) => dispatch(flightActions.changeCheckedItinerary(value === "round_trip"))}
                          suffixIcon={filterSuffixIcon}
                          options={[
                            {
                              label: t("flight.round_trip"),
                              value: "round_trip",
                            },
                            {
                              label: t("flight.one_way"),
                              value: "one_way",
                            },
                          ]}
                        />
                      </Col>
                    )}
                    <Col>
                      <SelectPeopleWrapper
                        size="middle"
                        variant="borderless"
                        style={{ marginTop: 0 }}
                        suffixIcon={filterSuffixIcon}
                        className="home__searching--select-people flights__formsearch--select-people"
                      />
                    </Col>
                    <Col>
                      <SelectCabinClass
                        size="middle"
                        variant="borderless"
                        style={{ marginTop: 0 }}
                        suffixIcon={filterSuffixIcon}
                        className="home__searching--select-cabin flights__formsearch--select-cabinclass"
                      />
                    </Col>
                  </Row>
                </Col>
                <Col>
                  {isMultiCities && (
                    <Button
                      size="large"
                      type="primary"
                      onClick={() => getFlightsAllAirlines({
                        onStart({ newSearchString }) {
                          router.push(`/flights/result${newSearchString}`)
                        },
                      })}
                    >
                      {t("flight.search_flight")}
                    </Button>
                  )}
                </Col>
              </Row>

              <Card className="flights__formsearch--content" variant="borderless" size="small">
                {inputFlights.map((input, index) => {
                  if (input.isReturn) {
                    return null
                  }
                  return (
                    <Row
                      key={index}
                      wrap={false}
                      gutter={12}
                      align="bottom"
                      className={`flights__formsearch--main-row${!isMultiCities ? " flights__formsearch--main-row--roundtrip" : ""}`}
                      style={index < (inputFlights.length - 1) ? { marginBottom: "1rem" } : undefined}
                    >
                      <Col flex="none" className="flights__formsearch--route-col">
                        <FlightSearchPopoverBoundary>
                          <div className="home__searching--interlock">
                            <div className="home__searching--interlock-field home__searching--interlock-field--from">
                              <SelectAirportFrom
                                onlyContent
                                layout="hero"
                                indexInputFlight={index}
                                size="small"
                              />
                            </div>
                            <div className="home__searching--interlock-swap-slot">
                              <Button
                                shape="circle"
                                className="home__searching--swap-btn home__searching--interlock-swap"
                                icon={<ArrowLeftRightIconDownIcon />}
                                onClick={() => dispatch(flightActions.swicthBtwAirports({ flightInputIndex: index }))}
                                aria-label={t("flight.from")}
                              />
                            </div>
                            <div className="home__searching--interlock-field home__searching--interlock-field--to">
                              <SelectAirportTo
                                onlyContent
                                layout="hero"
                                indexInputFlight={index}
                                size="small"
                              />
                            </div>
                          </div>
                        </FlightSearchPopoverBoundary>
                      </Col>
                      <Col flex="1 1 0" className="flights__formsearch--dates-col" style={{ minWidth: 0 }}>
                        <SelectDateFromToWrapper
                          size="small"
                          layout="hero"
                          compact
                          indexInputFlight={index}
                        />
                      </Col>
                      {!isMultiCities && (
                        <Col flex="none" className="flights__formsearch--button-col">
                          <Button
                            onClick={runSearch}
                            className="flights__formsearch--button"
                            type="primary"
                            size="large"
                            icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                          />
                        </Col>
                      )}
                    </Row>
                  )
                })}
              </Card>
            </>
          )}
        </Col>
      </Row>
      {stickyFlowSpacerHeight > 0 && (
        <div aria-hidden="true" style={{ height: stickyFlowSpacerHeight }} />
      )}
    </>
  )
}

export default FormSearchFlight